/**
 * Какая поверхность открыта.
 *
 * Главный способ — имя хоста: повар открывает `kitchen.`, кассир — `kassa.`.
 * Запасной — первый сегмент пути: на точке поддоменов нет, планшеты приходят
 * к мини-ПК по локальной сети на голый адрес вида `http://192.168.1.10:3000`,
 * и разводит их там `/kassa`, `/kitchen`, `/board`.
 *
 * Списки повторяют серверные (`src/domain/surfaces.ts`): передавать их с
 * сервера значило бы задерживать первую отрисовку ради пяти строк, которые
 * не меняются.
 */

export type Surface = "kitchen" | "courier" | "counter" | "admin" | "unknown";

const SURFACE_BY_PREFIX: Record<string, Surface> = {
  kitchen: "kitchen",
  courier: "courier",
  kassa: "counter",
  admin: "admin"
};

const PREFIX_BY_SURFACE: Record<Exclude<Surface, "unknown">, string> = {
  kitchen: "kitchen",
  courier: "courier",
  counter: "kassa",
  admin: "admin"
};

/** Имена поверхностей, которые может занимать первый сегмент пути. */
const PATH_PREFIXES = new Set([...Object.keys(SURFACE_BY_PREFIX), "board"]);

export interface SurfaceLocation {
  surface: Surface;
  /**
   * Общее начало всех внутренних адресов панели.
   *
   * На поддомене — пустая строка: панель живёт на корне. На точке — `/kassa`
   * или `/kitchen`, и роутер обязан его учитывать, иначе переход на «Заказы»
   * уведёт планшет на корень, где его экрана нет.
   */
  basePath: string;
}

function hasSubdomains(hostname: string): boolean {
  const name = hostname.trim().toLowerCase();
  if (name.startsWith("[") || name.includes(":")) return false;
  return !/^\d{1,3}(\.\d{1,3}){3}$/.test(name);
}

export function resolveSurface(location: Location = window.location): SurfaceLocation {
  const byHost = SURFACE_BY_PREFIX[location.hostname.toLowerCase().split(".")[0] ?? ""];
  if (byHost) return { surface: byHost, basePath: "" };

  const first = location.pathname.split("/")[1]?.toLowerCase() ?? "";
  if (!PATH_PREFIXES.has(first)) return { surface: "unknown", basePath: "" };

  return { surface: SURFACE_BY_PREFIX[first] ?? "unknown", basePath: `/${first}` };
}

/**
 * Начало адресов текущей поверхности, посчитанное один раз при загрузке.
 *
 * Пересчитывать его на каждый переход незачем: сменить поддомен или префикс
 * можно только полной перезагрузкой страницы.
 */
export const SURFACE_BASE = resolveSurface().basePath;

export function currentSurface(): Surface {
  return resolveSurface().surface;
}

/**
 * Адрес соседней поверхности.
 *
 * На домене собирается заменой первой метки, на голом IP — заменой первого
 * сегмента пути. Решает сам адрес, а не настройка: панель и так знает, где
 * работает, и второе место с этим знанием было бы лишним поводом их
 * рассинхронизировать.
 */
export function surfaceHref(surface: Exclude<Surface, "unknown">, path = "/"): string {
  const { protocol, hostname, port, origin } = window.location;
  const prefix = PREFIX_BY_SURFACE[surface];

  if (!hasSubdomains(hostname)) {
    return `${origin}/${prefix}${path === "/" ? "" : path}`;
  }

  const labels = hostname.split(".");
  const rest = labels.length > 1 && SURFACE_BY_PREFIX[labels[0]!] ? labels.slice(1) : labels;
  const host = [prefix, ...rest].join(".");

  return `${protocol}//${host}${port ? `:${port}` : ""}${path}`;
}

export const SURFACE_TITLES: Record<Exclude<Surface, "unknown">, string> = {
  kitchen: "Кухня",
  courier: "Доставка",
  counter: "Касса",
  admin: "Админка"
};
